import React, { useMemo, useRef } from 'react';
import type { Chart as ChartJS } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import KpiChartPanel from './KpiChartPanel';
import { KPI_PRIMARY_BLUE, KPI_ORANGE, KPI_SLATE } from './kpiChartPalette';

/** MSR slide palette — blue, orange, grey, yellow (matches Tenarai template). */
const MSR_SLICE_COLORS = [
  KPI_PRIMARY_BLUE,
  KPI_ORANGE,
  KPI_SLATE,
  '#eab308',
  '#94a3b8',
  '#0ea5e9',
];

export interface MsrDistributionDonutProps {
  title: string;
  rows: Array<{ label: string; count: number }>;
  emptyMessage?: string;
}

const MsrDistributionDonut: React.FC<MsrDistributionDonutProps> = ({
  title,
  rows,
  emptyMessage = 'No data for the selected customer and period.',
}) => {
  const chartRef = useRef<ChartJS<'doughnut'>>(null);
  const total = useMemo(() => rows.reduce((s, r) => s + r.count, 0), [rows]);

  if (!total) {
    return (
      <KpiChartPanel title={title} className="h-full" flexFill titleClassName="text-center">
        <p className="px-4 py-10 text-center text-sm text-slate-500">{emptyMessage}</p>
      </KpiChartPanel>
    );
  }

  return (
    <KpiChartPanel title={title} className="h-full" flexFill titleClassName="text-center">
      <div className="relative mx-auto flex min-h-[260px] max-w-md flex-1 flex-col items-center justify-center px-2 pb-2 pt-2">
        <Doughnut
          ref={chartRef}
          data={{
            labels: rows.map((r) => r.label),
            datasets: [
              {
                data: rows.map((r) => r.count),
                backgroundColor: rows.map((_, i) => MSR_SLICE_COLORS[i % MSR_SLICE_COLORS.length]),
                borderColor: '#ffffff',
                borderWidth: 2,
                hoverOffset: 6,
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            cutout: '52%',
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  boxWidth: 12,
                  padding: 10,
                  color: '#475569',
                  font: { size: 10 },
                  generateLabels: (chart) => {
                    const ds = chart.data.datasets[0];
                    const data = (ds?.data ?? []) as number[];
                    const labels = chart.data.labels ?? [];
                    return labels.map((label, i) => ({
                      text: `${String(label)} (${data[i]?.toLocaleString() ?? 0})`,
                      fillStyle: (Array.isArray(ds?.backgroundColor)
                        ? ds.backgroundColor[i]
                        : ds?.backgroundColor) as string,
                      strokeStyle: '#fff',
                      lineWidth: 0,
                      hidden: false,
                      index: i,
                    }));
                  },
                },
              },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const v = Number(ctx.raw);
                    const pct = total > 0 ? ((v / total) * 100).toFixed(1) : '0';
                    return `${ctx.label}: ${v.toLocaleString()} (${pct}%)`;
                  },
                },
              },
              datalabels: {
                display: true,
                color: '#fff',
                font: { weight: 'bold', size: 11 },
                formatter: (value: number) => {
                  const v = Number(value);
                  return v > 0 ? v.toLocaleString() : '';
                },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default MsrDistributionDonut;
